import { Box, Button, Divider, Location, Search } from "../../../../../../mads";
import { type Theme, useTheme, useThemedStylesheet } from "../../../../../../mads/ThemeProvider";
import { useI18n } from "../../../../../../shared/I18n";
import { useRnViewJobDisplay } from "../../../../context/ViewJobDisplayContext";

import { breakpoints } from "@indeed/ifl-tokens";
import React, { useState } from "react";
import { TextInput, type TextStyle, type ViewStyle } from "react-native";

type FocusedField = "what" | "where" | null;
type Styles = {
  pageContainer: ViewStyle;
  form: ViewStyle;
  searchShell: ViewStyle;
  field: ViewStyle;
  fieldFocused: ViewStyle;
  fieldDivider: ViewStyle;
  input: TextStyle;
  buttonWrapper: ViewStyle;
};

const RESPONSIVE_WRAPPER_CLASS = "rn-vj-standalone-desktop-search";
const responsiveStyles = `
  .${RESPONSIVE_WRAPPER_CLASS} {
    display: none;
  }

  @media (min-width: ${breakpoints.lg}) {
    .${RESPONSIVE_WRAPPER_CLASS} {
      /* Flex, not block: pageContainer is viewport-wide and relies on
         align-self to break out of the host's content column symmetrically. */
      display: flex;
      flex-direction: column;
    }
  }
`;

export function StandaloneDesktopSearchBox() {
  const i18n = useI18n();
  const theme = useTheme();
  const styles = useThemedStylesheet(stylesFactory);
  const isDesktopStandalone = useRnViewJobDisplay() === "DESKTOP_STANDALONE";
  const [what, setWhat] = useState("");
  const [where, setWhere] = useState("");
  const [focusedField, setFocusedField] = useState<FocusedField>(null);

  if (!isDesktopStandalone) {
    return null;
  }

  const submit = () => {
    const params = new URLSearchParams();
    const trimmedWhat = what.trim();
    const trimmedWhere = where.trim();
    if (trimmedWhat) {
      params.set("q", trimmedWhat);
    }
    if (trimmedWhere) {
      params.set("l", trimmedWhere);
    }
    const query = params.toString();
    window.location.assign(query ? `/jobs?${query}` : "/jobs");
  };

  return (
    <>
      <style>{responsiveStyles}</style>
      <div className={RESPONSIVE_WRAPPER_CLASS}>
        <Box testID="standalone-desktop-search-page" sx={styles.pageContainer}>
          <Box testID="standalone-desktop-search-form" sx={styles.form}>
            <Box testID="standalone-desktop-search-shell" sx={styles.searchShell}>
              <Box sx={[styles.field, focusedField === "what" && styles.fieldFocused]}>
                <Search
                  testID="standalone-desktop-search-what-icon"
                  aria-hidden
                  color={theme.colors.foregroundStrong}
                  size="sm"
                />
                <TextInput
                  testID="standalone-desktop-search-what"
                  accessibilityLabel={i18n.gettext("What")}
                  placeholder={i18n.gettext("Job title, keywords, or company")}
                  placeholderTextColor={theme.colors.foregroundWeak}
                  value={what}
                  onChangeText={setWhat}
                  onFocus={() => setFocusedField("what")}
                  onBlur={() => setFocusedField(null)}
                  onSubmitEditing={submit}
                  returnKeyType="search"
                  selectionColor={theme.colors.foregroundStrong}
                  style={styles.input}
                />
              </Box>
              <Box
                testID="standalone-desktop-search-divider"
                aria-hidden
                sx={styles.fieldDivider}
              />
              <Box sx={[styles.field, focusedField === "where" && styles.fieldFocused]}>
                <Location
                  testID="standalone-desktop-search-where-icon"
                  aria-hidden
                  color={theme.colors.foregroundStrong}
                  size="sm"
                />
                <TextInput
                  testID="standalone-desktop-search-where"
                  accessibilityLabel={i18n.gettext("Where")}
                  placeholder={i18n.gettext('City, state, zip code, or "remote"')}
                  placeholderTextColor={theme.colors.foregroundWeak}
                  value={where}
                  onChangeText={setWhere}
                  onFocus={() => setFocusedField("where")}
                  onBlur={() => setFocusedField(null)}
                  onSubmitEditing={submit}
                  returnKeyType="search"
                  selectionColor={theme.colors.foregroundStrong}
                  style={styles.input}
                />
              </Box>
              <Box sx={styles.buttonWrapper}>
                <Button
                  testID="standalone-desktop-search-submit"
                  onPress={submit}
                  variant="primary"
                  size="md"
                >
                  {i18n.gettext("Search")}
                </Button>
              </Box>
            </Box>
          </Box>
          <Divider />
        </Box>
      </div>
    </>
  );
}

const stylesFactory = (theme: Theme): Styles => ({
  pageContainer: {
    alignSelf: "center" as const,
    paddingTop: 0,
    width: "100vw" as ViewStyle["width"],
  },
  form: {
    alignSelf: "center" as const,
    maxWidth: 900,
    width: "100%",
    padding: theme.spacing.s5,
    paddingHorizontal: 0,
  },
  searchShell: {
    alignItems: "stretch" as const,
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.borderAction,
    borderRadius: 20,
    borderWidth: 1,
    boxShadow: theme.shadows.medium,
    flexDirection: "row" as const,
    padding: theme.spacing.s2,
    width: "100%",
  },
  field: {
    alignItems: "center" as const,
    borderRadius: theme.borderRadius.md,
    flex: 1,
    flexDirection: "row" as const,
    gap: theme.spacing.s3,
    minWidth: 0,
    paddingHorizontal: theme.spacing.s3,
  },
  fieldFocused: {
    boxShadow: `0 0 0 3px ${theme.colors.primary800}`,
    zIndex: 1,
  },
  fieldDivider: {
    alignSelf: "center" as const,
    backgroundColor: theme.colors.borderWeak,
    height: 32,
    width: 1,
  },
  input: {
    borderWidth: 0,
    color: theme.colors.foregroundStrong,
    flex: 1,
    fontFamily: theme.fonts.default,
    fontSize: theme.fontSizes.f3,
    height: 48,
    minWidth: 0,
    padding: 0,
    textAlignVertical: "center" as const,
  },
  buttonWrapper: {
    flexShrink: 0,
    justifyContent: "center" as const,
    marginLeft: theme.spacing.s2,
  },
});
